/**
 * QueueMonitor Component
 *
 * Shows pending execution queue and upcoming scheduled tests.
 * Split panel with queue depth and schedule preview.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useVisibilityAwareInterval } from '@/hooks/useVisibilityAwareInterval';
import { useNavigate } from 'react-router-dom';
import {
  ListOrdered,
  Calendar,
  Clock,
  PlayCircle,
  Pause,
  ChevronRight,
  AlertTriangle,
  Loader2,
  RotateCcw,
} from 'lucide-react';
import { Card, Badge, Spinner, Button } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { toDate } from '@/lib/date-utils';
import { db } from '@/config/firebase';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { formatDistanceToNow, format, isToday, isTomorrow } from 'date-fns';

interface QueueItem {
  id: string;
  flowName: string;
  priority: number;
  status: 'pending' | 'running' | 'failed';
  queuedAt: Date;
  attempts: number;
  maxAttempts: number;
}

interface ScheduleItem {
  id: string;
  name: string;
  flowName: string;
  nextRun: Date;
  type: 'once' | 'recurring';
  status: 'active' | 'paused';
}

interface QueueStats {
  pending: number;
  running: number;
  failed: number;
  avgWaitTime: number; // minutes
}

interface QueueMonitorProps {
  maxQueueItems?: number;
  maxScheduleItems?: number;
}

/**
 * Priority badge color
 */
function getPriorityColor(priority: number): string {
  if (priority >= 8) return 'bg-status-error-bg text-status-error';
  if (priority >= 5) return 'bg-status-warning-bg text-status-warning';
  return 'bg-muted text-muted-foreground';
}

/**
 * Format schedule time for display
 */
function formatScheduleTime(date: Date): string {
  if (isToday(date)) {
    return `Today at ${format(date, 'HH:mm')}`;
  }
  if (isTomorrow(date)) {
    return `Tomorrow at ${format(date, 'HH:mm')}`;
  }
  return format(date, 'MMM d, HH:mm');
}

export const QueueMonitor: React.FC<QueueMonitorProps> = ({
  maxQueueItems = 5,
  maxScheduleItems = 5,
}) => {
  const navigate = useNavigate();
  const { activeOrganization } = useAuth();

  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
  const [scheduleItems, setScheduleItems] = useState<ScheduleItem[]>([]);
  const [stats, setStats] = useState<QueueStats>({
    pending: 0,
    running: 0,
    failed: 0,
    avgWaitTime: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!activeOrganization?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Load queue items
      const queueQuery = query(
        collection(db, 'execution_queue'),
        where('organizationId', '==', activeOrganization.id),
        where('status', 'in', ['pending', 'running', 'failed']),
        orderBy('priority', 'desc'),
        orderBy('queuedAt', 'asc'),
        limit(maxQueueItems)
      );
      const queueSnapshot = await getDocs(queueQuery);
      const queueData: QueueItem[] = queueSnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          flowName: data.flowName || 'Unknown Flow',
          priority: data.priority || 5,
          status: data.status || 'pending',
          queuedAt: data.queuedAt ? toDate(data.queuedAt) || new Date() : new Date(),
          attempts: data.attempts || 0,
          maxAttempts: data.maxAttempts || 3,
        };
      });
      setQueueItems(queueData);

      // Calculate stats
      const pendingCount = queueData.filter((q) => q.status === 'pending').length;
      const runningCount = queueData.filter((q) => q.status === 'running').length;
      const failedCount = queueData.filter((q) => q.status === 'failed').length;

      // Calculate average wait time for pending items
      const now = new Date();
      const waitTimes = queueData
        .filter((q) => q.status === 'pending')
        .map((q) => (now.getTime() - q.queuedAt.getTime()) / 60000);
      const avgWaitTime = waitTimes.length > 0
        ? Math.round(waitTimes.reduce((a, b) => a + b, 0) / waitTimes.length)
        : 0;

      setStats({
        pending: pendingCount,
        running: runningCount,
        failed: failedCount,
        avgWaitTime,
      });

      // Load upcoming scheduled tests (one-off scheduled test_runs). The legacy
      // test_schedules collection was retired — scheduling now lives on the
      // /tests page (test_runs.scheduledFor + recurring test_configurations,
      // fired by processTestSchedules).
      const nowTs = Timestamp.fromDate(new Date());
      const schedulesQuery = query(
        collection(db, 'test_runs'),
        where('organizationId', '==', activeOrganization.id),
        where('status', '==', 'initializing'),
        where('scheduledFor', '>=', nowTs),
        orderBy('scheduledFor', 'asc'),
        limit(maxScheduleItems)
      );
      const schedulesSnapshot = await getDocs(schedulesQuery);
      const scheduleData: ScheduleItem[] = schedulesSnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.configuration?.name || data.flowName || 'Scheduled test',
          flowName: data.flowName || 'Unknown Flow',
          nextRun: data.scheduledFor ? toDate(data.scheduledFor) || new Date() : new Date(),
          type: data.triggerSource === 'recurring' ? 'recurring' : 'once',
          status: 'active',
        };
      });
      setScheduleItems(scheduleData);

      setLoading(false);
    } catch (err) {
      console.error('Error loading queue data:', err);
      setError('Failed to load queue data');
      setLoading(false);
    }
  }, [activeOrganization?.id, maxQueueItems, maxScheduleItems]);

  // Initial load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Visibility-aware polling: pauses when tab is hidden
  useVisibilityAwareInterval(loadData, 60000, true);

  if (loading) {
    return (
      <Card padding="lg">
        <div className="flex items-center justify-center py-8">
          <Spinner size="md" />
          <span className="ml-2 text-muted-foreground">Loading queue...</span>
        </div>
      </Card>
    );
  }

  return (
    <Card padding="lg">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-h3 font-semibold text-foreground">
            Queue & Schedules
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Pending work and upcoming tests
          </p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="p-2 rounded bg-muted text-center">
          <div className="text-lg font-semibold text-foreground">{stats.pending}</div>
          <div className="text-xs text-muted-foreground">Pending</div>
        </div>
        <div className="p-2 rounded bg-accent-blue/10 text-center">
          <div className="text-lg font-semibold text-accent-blue">{stats.running}</div>
          <div className="text-xs text-muted-foreground">Running</div>
        </div>
        {stats.failed > 0 && (
          <div className="p-2 rounded bg-status-error-bg text-center">
            <div className="text-lg font-semibold text-status-error">{stats.failed}</div>
            <div className="text-xs text-muted-foreground">Retry</div>
          </div>
        )}
        {stats.failed === 0 && (
          <div className="p-2 rounded bg-muted text-center">
            <div className="text-lg font-semibold text-foreground">
              {stats.avgWaitTime > 0 ? `${stats.avgWaitTime}m` : '--'}
            </div>
            <div className="text-xs text-muted-foreground">Avg Wait</div>
          </div>
        )}
      </div>

      {/* Queue Section */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <ListOrdered className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">Execution Queue</span>
        </div>

        {queueItems.length === 0 ? (
          <div className="text-sm text-muted-foreground py-2 text-center bg-muted/50 rounded">
            Queue is empty
          </div>
        ) : (
          <div className="space-y-2">
            {queueItems.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-2 p-2 rounded border border-border hover:bg-muted/50 transition-colors"
              >
                {item.status === 'running' ? (
                  <Loader2 className="w-4 h-4 text-accent-blue animate-spin flex-shrink-0" />
                ) : item.status === 'failed' ? (
                  <RotateCcw className="w-4 h-4 text-status-warning flex-shrink-0" />
                ) : (
                  <Clock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                )}

                <span className="text-sm text-foreground truncate flex-1">
                  {item.flowName}
                </span>

                <Badge className={`${getPriorityColor(item.priority)} text-xs px-1.5`}>
                  P{item.priority}
                </Badge>

                {item.attempts > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {item.attempts}/{item.maxAttempts}
                  </span>
                )}

                <span className="text-xs text-muted-foreground/70 whitespace-nowrap">
                  {format(item.queuedAt, 'MMM d, HH:mm')} · {formatDistanceToNow(item.queuedAt, { addSuffix: true })}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-border my-4" />

      {/* Schedules Section */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">Upcoming Schedules</span>
        </div>

        {scheduleItems.length === 0 ? (
          <div className="text-sm text-muted-foreground py-2 text-center bg-muted/50 rounded">
            No upcoming schedules
          </div>
        ) : (
          <div className="space-y-2">
            {scheduleItems.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-2 p-2 rounded border border-border hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={() => navigate('/tests?tab=upcoming')}
              >
                <PlayCircle className="w-4 h-4 text-accent-green flex-shrink-0" />

                <div className="flex-1 min-w-0">
                  <span className="text-sm text-foreground truncate block">
                    {item.name}
                  </span>
                  <span className="text-xs text-muted-foreground truncate block">
                    {item.flowName}
                  </span>
                </div>

                {item.type === 'recurring' && (
                  <Badge className="bg-accent-purple/10 text-accent-purple text-xs px-1.5">
                    Recurring
                  </Badge>
                )}

                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatScheduleTime(item.nextRun)}
                </span>

                <ChevronRight className="w-4 h-4 text-muted-foreground/50" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* View all link */}
      <div className="mt-4 pt-4 border-t border-border flex justify-end">
        <button
          onClick={() => navigate('/tests')}
          className="text-sm text-accent-blue hover:text-accent-blue/80 transition-colors"
        >
          View all in Tests
        </button>
      </div>
    </Card>
  );
};

export default QueueMonitor;
